/**
 * Channel-aware titles for the AI Assist live-progress sections.
 *
 * Section ids stay the same across Gmail, Slack, CRM, calendar, and any future
 * channel. Only the user-facing titles change. Unknown types use GENERIC so a
 * new channel still looks sane instead of inheriting Gmail copy.
 */

const PROGRESS_SECTION_IDS = ["setup", "context", "files", "retries", "drafts", "save"];

const GENERIC_PROGRESS_SECTION_TITLES = {
  setup: "Getting ready",
  context: "Gathering context",
  files: "Reading your documents",
  retries: "Searching for more documents",
  drafts: "Working on a suggestion",
  save: "Saving",
};

/**
 * Per-channel overlays on GENERIC_PROGRESS_SECTION_TITLES.
 * Add a new key here when a new ai_assist_type gets its own draft surface.
 */
const PROGRESS_SECTION_TITLE_OVERLAYS = {
  email: {
    context: "Reading your emails & documents",
    save: "Saving to Gmail",
  },
  slack: {
    context: "Reading your messages & documents",
    drafts: "Working on a reply",
    save: "Preparing your Slack reply",
  },
  crm: {
    context: "Looking up CRM records",
    retries: "Searching for more records",
    drafts: "Working on a CRM update",
    save: "Preparing your CRM update",
  },
  calendar: {
    context: "Checking your calendar",
    drafts: "Working on an invite",
    save: "Preparing your calendar invite",
  },
};

/**
 * Resolve the progress-section titles for one classified assist type.
 *
 * @example
 * progressSectionTitlesFor("crm").context
 * // "Looking up CRM records"
 *
 * @example
 * progressSectionTitlesFor("email").context
 * // "Reading your emails & documents"
 *
 * @example
 * progressSectionTitlesFor("").context
 * // "Gathering context"
 */
export const progressSectionTitlesFor = (aiAssistType) => {
  const channel = String(aiAssistType || "").trim().toLowerCase();
  const overlay = PROGRESS_SECTION_TITLE_OVERLAYS[channel] || {};
  return { ...GENERIC_PROGRESS_SECTION_TITLES, ...overlay };
};

/**
 * Infer assist type from live progress events when the item type was not passed.
 *
 * CRM / Slack / calendar labels are checked before Gmail so a CRM lookup that
 * happens to mention an email address is not classified as an email run.
 *
 * @example
 * inferAssistTypeFromSteps([{ phase: "retrieving_context", label: "Checking Attio for a matching record" }])
 * // "crm"
 *
 * @example
 * inferAssistTypeFromSteps([{ phase: "gmail_context", label: "Looking at your recent conversations" }])
 * // "email"
 */
export const inferAssistTypeFromSteps = (steps = []) => {
  const haystack = (steps || [])
    .map((step) => `${step?.phase || ""} ${step?.label || ""}`.toLowerCase())
    .join(" ");
  if (!haystack.trim()) return "";
  if (/\bcrm\b|\battio\b|\bsalesforce\b/.test(haystack)) return "crm";
  if (/\bslack\b/.test(haystack)) return "slack";
  if (/\bcalendar\b|\binvite\b/.test(haystack)) return "calendar";
  if (/\bgmail\b|\bemail/.test(haystack) || haystack.includes("gmail_context")) return "email";
  return "";
};

/**
 * Group a low-level progress event into a human-readable UI section.
 *
 * @example
 * resolveProgressSectionId({ phase: "drive_export_3" })
 * // "files"
 *
 * @example
 * resolveProgressSectionId({ phase: "retrieving_context" })
 * // "context"
 */
export const resolveProgressSectionId = (step) => {
  const phase = String(step?.phase || "");
  if (phase.startsWith("drive_refinement_export_")) return "retries";
  if (phase.startsWith("drive_export_")) return "files";
  if (phase.startsWith("drive_refinement")) return "retries";
  if (phase.startsWith("drafting") || phase.startsWith("evaluating")) return "drafts";
  if (phase.startsWith("saving_draft")) return "save";
  if (
    phase.startsWith("gmail_context") ||
    phase.startsWith("drive_") ||
    phase.startsWith("uploaded_context") ||
    phase.startsWith("retrieving_context")
  ) {
    return "context";
  }
  return "setup";
};

/**
 * Build collapsible progress sections from raw performer events.
 *
 * Titles follow `aiAssistType` when provided; otherwise they are inferred from
 * the steps so CRM/Slack runs do not keep Gmail headings.
 *
 * @example
 * buildProgressSections(
 *   [{ phase: "retrieving_context", label: "Checking Attio", status: "done" }],
 *   "crm"
 * )
 * // [{ id: "context", title: "Looking up CRM records", status: "done", ... }]
 *
 * @example
 * buildProgressSections(
 *   [{ phase: "gmail_context", label: "Looking at your recent conversations", status: "done" }],
 *   "email"
 * )
 * // [{ id: "context", title: "Reading your emails & documents", status: "done", ... }]
 */
export const buildProgressSections = (steps, aiAssistType) => {
  const channel = String(aiAssistType || "").trim() || inferAssistTypeFromSteps(steps);
  const titles = progressSectionTitlesFor(channel);
  const sectionMap = Object.fromEntries(
    PROGRESS_SECTION_IDS.map((id) => [
      id,
      { id, title: titles[id], steps: [], status: "pending", summary: "" },
    ])
  );

  (steps || []).forEach((step) => {
    const sectionId = resolveProgressSectionId(step);
    sectionMap[sectionId].steps.push(step);
  });

  return PROGRESS_SECTION_IDS.map((id) => {
    const section = sectionMap[id];
    if (section.steps.length === 0) return null;
    const latest = section.steps[section.steps.length - 1] || {};
    const running = section.steps.find((step) => step?.status === "running");
    const error = section.steps.find((step) => step?.status === "error");
    const status = error ? "error" : running ? "running" : "done";
    return {
      ...section,
      status,
      summary: String((running || latest)?.label || "").trim(),
    };
  }).filter(Boolean);
};
